/**
 * CRM patient modal layout + clinic-prefill contract (2026-08-25).
 *
 * Layout (top → bottom), pinned in crmPatientModal.test.js against the
 * modal's source so a future edit can't quietly re-order it:
 *   1. patient header … patient phone   (always visible)
 *   2. the working block                (always open, never collapsible)
 *   3. reference sections               (all collapsed on every open)
 *   4. View Documents                   (last content block)
 */

export const WORKING_BLOCK_ORDER = [
  "appointments-banner",   // purple "Scheduled Appointments"
  "location-dropdown",     // clinic dropdown
  "banner-status-wrapper",  // status dropdown
  "banner-set-callback",
  "schedule-btn",          // date-range picker + Schedule
  "schedule-network-btn",
  "patient-notes-input",
];

export const REFERENCE_SECTIONS = [
  { id: "email",     title: "Patient Email" },
  { id: "physician", title: "Referring Physician" },
  { id: "address",   title: "Patient Address" },
  { id: "case",      title: "Case & Insurance Details" },
  { id: "icd10",     title: "ICD-10 Codes" },
];

const norm = (v) => String(v || "").toLowerCase().trim();

/**
 * Which clinic the dropdown shows when the modal opens.
 *
 * Priority 0 (NEW): the patient's CURRENT ASSIGNMENT. Before this, the
 * dropdown only ever showed a *recommendation* (raintree code → city →
 * first option), so an already-assigned patient opened on the wrong
 * clinic. Assignment mechanics are untouched — this is display state.
 * After a reassignment the caller's `assignedClinic` is updated
 * locally (read-your-own-write), so this resolves to the new clinic.
 */
export function pickPrefilledLocation({
  locations = [],
  assignedClinic = null,
  assignedCity = null,
  raintreeCode = null,
  patientCity = null,
} = {}) {
  if (!locations.length) return { location: null, matchType: null };

  if (assignedClinic) {
    const name = norm(assignedClinic);
    const city = norm(assignedCity);
    const byNameAndCity = locations.find(
      (l) => (norm(l.clinic_name) === name || norm(l.name) === name)
        && (!city || norm(l.city) === city),
    );
    const hit = byNameAndCity
      || locations.find((l) => norm(l.clinic_name) === name || norm(l.name) === name);
    if (hit) return { location: hit, matchType: "assigned" };
  }

  if (raintreeCode) {
    const hit = locations.find((l) => l.raintree_code === raintreeCode);
    if (hit) return { location: hit, matchType: "code" };
  }

  const city = norm(patientCity);
  if (city) {
    const hit = locations.find((l) => {
      const lc = norm(l.city);
      return lc === city || (lc && lc.includes(city)) || (lc && city.includes(lc));
    });
    if (hit) return { location: hit, matchType: "city" };
  }

  return { location: locations[0], matchType: "fallback" };
}
